/**
 * Active Tab Context
 * 
 * Provides information about whether the current component is in the active tab.
 * Components can use this to avoid making API calls when they're in a background tab.
 */

import React, { createContext, useContext } from 'react';

type ActiveTabContextValue = {
  isActive: boolean;
  tabKey: string | null;
  paneId?: string;
};

const ActiveTabContext = createContext<ActiveTabContextValue>({
  isActive: true,
  tabKey: null,
});

export const ActiveTabProvider: React.FC<{
  isActive: boolean;
  tabKey: string | null;
  paneId?: string;
  children: React.ReactNode;
}> = ({ isActive, tabKey, paneId, children }) => {
  return (
    <ActiveTabContext.Provider value={{ isActive, tabKey, paneId }}>
      {children}
    </ActiveTabContext.Provider>
  );
};

/**
 * Hook to check if the current component is in the active tab.
 * Use this to conditionally skip API calls when in background tabs.
 */
export const useIsActiveTab = () => {
  const context = useContext(ActiveTabContext);
  return context.isActive;
};

/**
 * Hook to get the current tab key
 */
export const useActiveTabKey = () => {
  const context = useContext(ActiveTabContext);
  return context.tabKey;
};

export const useActiveTabContext = () => useContext(ActiveTabContext);

export default ActiveTabContext;
